'use client'

import { useEffect, useState } from 'react'
import { isChunkLoadError, reloadOnceForChunkError } from '../lib/chunkReload'

// Last-resort boundary for errors in the root layout itself. It replaces the
// whole document, so none of the site's CSS is loaded: styles are inline.
const styles = {
  body: {
    margin: 0,
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    padding: '1.5rem',
    background: '#f5f9ff',
    color: '#10253f',
    fontFamily: "'DM Sans', Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
  },
  card: { maxWidth: '30rem' },
  title: { fontSize: '1.75rem', lineHeight: 1.2, margin: '0 0 .75rem' },
  text: { color: '#60738b', lineHeight: 1.6, margin: '0 0 1.5rem' },
  actions: { display: 'flex', flexWrap: 'wrap' as const, gap: '.75rem' },
  primary: {
    border: 0,
    borderRadius: 999,
    padding: '.8rem 1.4rem',
    font: 'inherit',
    fontWeight: 700,
    color: '#fff',
    background: 'linear-gradient(90deg, #1895ff, #022646)',
    cursor: 'pointer',
  },
  secondary: {
    borderRadius: 999,
    padding: '.8rem 1.4rem',
    fontWeight: 700,
    color: '#0a66cc',
    border: '1px solid rgba(22,132,234,.3)',
    background: '#fff',
    textDecoration: 'none',
  },
}

export default function GlobalError({ error, retry }: { error: Error & { digest?: string }; retry: () => void }) {
  const staleBuild = isChunkLoadError(error)
  const [reloading, setReloading] = useState(false)

  useEffect(() => {
    console.error(error)
    if (reloadOnceForChunkError(error)) setReloading(true)
  }, [error])

  return (
    <html lang="en">
      <body style={styles.body}>
        <title>GHL Prime</title>
        <div style={styles.card}>
          {reloading ? (
            <>
              <h1 style={styles.title}>Loading the latest version</h1>
              <p style={styles.text}>The site was updated while this tab was open. One moment.</p>
            </>
          ) : (
            <>
              <h1 style={styles.title}>This page didn&rsquo;t finish loading</h1>
              <p style={styles.text}>
                {staleBuild
                  ? 'The site was updated while this tab was open. Reload the page to get the latest version.'
                  : 'Something on this page failed to load. Try again, or head back to the homepage.'}
              </p>
              <div style={styles.actions}>
                <button
                  type="button"
                  style={styles.primary}
                  onClick={() => (staleBuild ? window.location.reload() : retry())}
                >
                  {staleBuild ? 'Reload page' : 'Try again'}
                </button>
                {/* Plain <a>: a full page load is the point here, not a client-side navigation. */}
                <a href="/" style={styles.secondary}>Back to homepage</a>
              </div>
            </>
          )}
        </div>
      </body>
    </html>
  )
}
